import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, DatePicker, Form, Input, InputNumber, Modal, Table, Typography } from 'antd';
import moment from 'moment';
import { isMobile } from 'react-device-detect';
import { useDispatch } from 'react-redux';
import { applyCustomerBalance } from '@/redux/actions/receiptActions';
import { renderMoney } from '@/utils/helpers';

const moneyFormatter = value => `${value == null ? '' : value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
const createKey = customerId => `CUSTOMER_BALANCE_UI:${customerId}:${Date.now()}:${Math.random().toString(36).slice(2)}`;

/** Áp dụng số dư cho dịch vụ mà không tạo Phiếu thu mới. */
export default function CustomerBalanceApplicationModal({ visible, customerId, finance, onCancel, onCompleted }) {
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const [amounts, setAmounts] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const services = useMemo(
        () => (finance.services || []).filter(service => Number(service.outstanding_amount) > 0),
        [finance.services]
    );
    const total = Object.values(amounts).reduce((sum, value) => sum + Number(value || 0), 0);
    const balance = Number(finance.unallocated_balance || 0);

    useEffect(() => {
        if (!visible) return;
        setAmounts({});
        form.setFieldsValue({ applied_at: moment(), reason: 'Sử dụng số dư khách hàng cho dịch vụ.' });
    }, [visible, form]);

    const setServiceAmount = (service, value) => {
        const amount = Math.max(0, Math.min(Number(value || 0), Number(service.outstanding_amount || 0)));
        setAmounts(current => ({ ...current, [service.id]: amount }));
    };

    const submit = async () => {
        const values = await form.validateFields();
        const allocations = services
            .map(service => ({ customer_service_id: Number(service.id), amount: Number(amounts[service.id] || 0) }))
            .filter(item => item.amount > 0);
        if (!allocations.length || total <= 0 || total > balance) return;

        setSubmitting(true);
        try {
            await dispatch(applyCustomerBalance({
                customer_id: Number(customerId),
                allocations,
                applied_at: values.applied_at.format('YYYY-MM-DD HH:mm:ss'),
                reason: values.reason.trim(),
                idempotency_key: createKey(customerId),
            }));
            if (onCompleted) await onCompleted();
            onCancel();
        } finally {
            setSubmitting(false);
        }
    };

    return <Modal
        visible={visible}
        title="Sử dụng số dư cho dịch vụ"
        width={isMobile ? '100%' : '90%'}
        maskClosable={false}
        destroyOnClose
        onCancel={onCancel}
        footer={[
            <Button key="cancel" onClick={onCancel}>Hủy</Button>,
            <Button key="submit" type="primary" loading={submitting} disabled={total <= 0 || total > balance}
                onClick={submit}>Sử dụng số dư</Button>,
        ]}
    >
        <Alert type="info" message={<>Số dư khả dụng: <Typography.Text strong>{renderMoney(balance)}</Typography.Text>. Thao tác này không tạo Phiếu thu mới.</>} />
        <Table
            style={{ marginTop: 12 }}
            size="small"
            rowKey="id"
            pagination={false}
            scroll={{ x: 'max-content' }}
            dataSource={services}
            locale={{ emptyText: 'Không có dịch vụ còn công nợ.' }}
            columns={[
                { title: 'Dịch vụ', dataIndex: 'name', width: 260 },
                { title: 'Đã phân bổ', dataIndex: 'allocated_amount', align: 'right', render: renderMoney },
                { title: 'Đã ghi nhận hoa hồng', dataIndex: 'commission_recognized_amount', align: 'right', render: renderMoney },
                { title: 'Còn chờ ghi nhận', dataIndex: 'commission_pending_amount', align: 'right', render: renderMoney },
                { title: 'Lý do chờ', dataIndex: 'commission_pending_reason', width: 260, render: value => value || '—' },
                { title: 'Công nợ còn lại', dataIndex: 'outstanding_amount', align: 'right', render: renderMoney },
                {
                    title: 'Số tiền sử dụng',
                    width: 200,
                    render: (_, service) => <InputNumber
                        style={{ width: '100%' }}
                        min={0}
                        max={Math.min(balance, Number(service.outstanding_amount || 0))}
                        precision={0}
                        formatter={moneyFormatter}
                        parser={value => String(value || '').replace(/,/g, '')}
                        value={amounts[service.id] || 0}
                        onChange={value => setServiceAmount(service, value)}
                    />,
                },
            ]}
        />
        <Typography.Paragraph style={{ marginTop: 12, textAlign: 'right' }}>
            Tổng sử dụng: <Typography.Text strong type={total > balance ? 'danger' : undefined}>{renderMoney(total)}</Typography.Text>
        </Typography.Paragraph>
        {total > balance && <Alert type="error" message="Tổng số tiền sử dụng vượt số dư khả dụng." />}
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
            <Form.Item name="applied_at" label="Ngày áp dụng" rules={[{ required: true, message: 'Vui lòng chọn ngày áp dụng.' }]}>
                <DatePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="reason" label="Lý do" rules={[{ required: true, whitespace: true, message: 'Vui lòng nhập lý do.' }]}>
                <Input.TextArea rows={3} maxLength={500} />
            </Form.Item>
        </Form>
    </Modal>;
}
